import { PayCurveConfigSchema, PayCurveSimulationProfilesSchema } from './schemas'

import type {
  PayCurveAttainmentBin,
  PayCurveConfig,
  PayCurveMarginalPoint,
  PayCurveMetrics,
  PayCurvePayout,
  PayCurvePoint,
  PayCurveProfileCompensationDelta,
  PayCurveSimulationAnalysis,
  PayCurveSimulationComparison,
  PayCurveSimulationProfile,
  PayCurveValidationIssue,
  PayCurveValidationResult,
  ResolvedPayCurvePoint
} from './types'

const EPSILON = 0.0001
type ResolvedPayCurvePointWithSource = ResolvedPayCurvePoint & {
  sourcePayout: PayCurvePayout
}

type FormulaToken =
  | { kind: 'number'; value: string }
  | { kind: 'identifier' | 'operator' | 'string'; value: string }

const isFiniteNumber = (value: number) => Number.isFinite(value)

const clampNonNegative = (value: number) => Math.max(0, value)

const tokenizeFormula = (formula: string): FormulaToken[] | null => {
  const tokens: FormulaToken[] = []
  let index = 0

  while (index < formula.length) {
    const character = formula[index]

    if (/\s/.test(character)) {
      index += 1
      continue
    }

    if (/[0-9.]/.test(character)) {
      const start = index
      index += 1
      while (index < formula.length && /[0-9.]/.test(formula[index])) index += 1
      const value = formula.slice(start, index)
      if (!Number.isFinite(Number(value))) return null
      tokens.push({ kind: 'number', value })
      continue
    }

    if (/[A-Za-z_]/.test(character)) {
      const start = index
      index += 1
      while (index < formula.length && /[A-Za-z0-9_]/.test(formula[index])) index += 1
      tokens.push({ kind: 'identifier', value: formula.slice(start, index) })
      continue
    }

    if (character === "'") {
      const start = index + 1
      index += 1
      while (index < formula.length && formula[index] !== "'") index += 1
      if (index >= formula.length) return null
      tokens.push({ kind: 'string', value: formula.slice(start, index) })
      index += 1
      continue
    }

    const twoCharacterOperator = formula.slice(index, index + 2)
    if (['>=', '<=', '<>', '!='].includes(twoCharacterOperator)) {
      tokens.push({ kind: 'operator', value: twoCharacterOperator })
      index += 2
      continue
    }

    if ('+-*/%^=<>'.includes(character)) {
      tokens.push({ kind: 'operator', value: character })
      index += 1
      continue
    }

    if ('(),'.includes(character)) {
      tokens.push({ kind: 'operator', value: character })
      index += 1
      continue
    }

    return null
  }

  return tokens
}

class PayCurveFormulaParser {
  private index = 0

  constructor(
    private readonly tokens: FormulaToken[],
    private readonly context: Record<string, number>
  ) {}

  parse(): number | null {
    const result = this.parseOr()
    return this.index === this.tokens.length && result != null && Number.isFinite(result)
      ? result
      : null
  }

  private current() {
    return this.tokens[this.index]
  }

  private consume(value?: string) {
    const token = this.current()
    if (!token || (value != null && token.value.toUpperCase() !== value.toUpperCase())) {
      return null
    }
    this.index += 1
    return token
  }

  private parseOr(): number | null {
    let value = this.parseAnd()
    while (this.current()?.kind === 'identifier' && this.current()?.value.toUpperCase() === 'OR') {
      this.index += 1
      const right = this.parseAnd()
      value = (value != null && value !== 0) || (right != null && right !== 0) ? 1 : 0
    }
    return value
  }

  private parseAnd(): number | null {
    let value = this.parseComparison()
    while (this.current()?.kind === 'identifier' && this.current()?.value.toUpperCase() === 'AND') {
      this.index += 1
      const right = this.parseComparison()
      value = value != null && value !== 0 && right != null && right !== 0 ? 1 : 0
    }
    return value
  }

  private parseComparison(): number | null {
    const left = this.parseAdditive()
    const token = this.current()

    if (!token) return left

    if (token.kind === 'identifier' && token.value.toUpperCase() === 'IS') {
      this.index += 1
      const isNot =
        this.current()?.kind === 'identifier' && this.current()?.value.toUpperCase() === 'NOT'
      if (isNot) this.index += 1
      const isNull =
        this.current()?.kind === 'identifier' && this.current()?.value.toUpperCase() === 'NULL'
      if (!isNull) return null
      this.index += 1
      const result = left == null
      return isNot ? (result ? 0 : 1) : result ? 1 : 0
    }

    if (
      token.kind !== 'operator' ||
      !['=', '!=', '<>', '<', '>', '<=', '>='].includes(token.value)
    ) {
      return left
    }

    this.index += 1
    const right = this.parseAdditive()
    if (left == null || right == null) return null

    switch (token.value) {
      case '=':
        return left === right ? 1 : 0
      case '!=':
      case '<>':
        return left !== right ? 1 : 0
      case '<':
        return left < right ? 1 : 0
      case '>':
        return left > right ? 1 : 0
      case '<=':
        return left <= right ? 1 : 0
      case '>=':
        return left >= right ? 1 : 0
      default:
        return null
    }
  }

  private parseAdditive(): number | null {
    let value = this.parseMultiplicative()

    while (
      this.current()?.kind === 'operator' &&
      ['+', '-'].includes(this.current()?.value ?? '')
    ) {
      const operator = this.current()?.value
      this.index += 1
      const right = this.parseMultiplicative()
      if (value == null || right == null) {
        value = null
      } else {
        value = operator === '+' ? value + right : value - right
      }
    }

    return value
  }

  private parseMultiplicative(): number | null {
    let value = this.parseUnary()

    while (
      this.current()?.kind === 'operator' &&
      ['*', '/', '%', '^'].includes(this.current()?.value ?? '')
    ) {
      const operator = this.current()?.value
      this.index += 1
      const right = this.parseUnary()
      if (value == null || right == null) {
        value = null
        continue
      }
      if ((operator === '/' || operator === '%') && right === 0) {
        value = null
        continue
      }
      if (operator === '*') value *= right
      if (operator === '/') value /= right
      if (operator === '%') value %= right
      if (operator === '^') value = Math.pow(value, right)
    }

    return value != null && Number.isFinite(value) ? value : null
  }

  private parseUnary(): number | null {
    const token = this.current()
    if (token?.kind === 'operator' && ['+', '-'].includes(token.value)) {
      this.index += 1
      const value = this.parseUnary()
      return value == null ? null : token.value === '-' ? -value : value
    }
    if (token?.kind === 'identifier' && token.value.toUpperCase() === 'NOT') {
      this.index += 1
      const value = this.parseUnary()
      return value == null || value === 0 ? 1 : 0
    }
    return this.parsePrimary()
  }

  private parsePrimary(): number | null {
    const token = this.current()
    if (!token) return null

    if (token.kind === 'number') {
      this.index += 1
      return Number(token.value)
    }

    if (token.kind === 'string') {
      this.index += 1
      return null
    }

    if (token.kind === 'operator' && token.value === '(') {
      this.index += 1
      const value = this.parseOr()
      if (!this.consume(')')) return null
      return value
    }

    if (token.kind !== 'identifier') return null

    const identifier = token.value.toUpperCase()
    this.index += 1

    if (identifier === 'CASE') return this.parseCase()
    if (identifier === 'NULL') return null
    if (identifier === 'TRUE') return 1
    if (identifier === 'FALSE') return 0

    if (this.current()?.kind === 'operator' && this.current()?.value === '(') {
      this.index += 1
      const args: Array<number | null> = []
      if (this.current()?.value !== ')') {
        while (true) {
          args.push(this.parseOr())
          if (this.current()?.value !== ',') break
          this.index += 1
        }
      }
      if (!this.consume(')')) return null
      return this.applyFunction(identifier, args)
    }

    if (identifier === 'ACHIEVEMENT') return this.context.attainment
    return this.context[identifier.toLowerCase()] ?? null
  }

  private parseCase(): number | null {
    let matched: number | null = null
    let hasMatch = false

    while (
      this.current()?.kind === 'identifier' &&
      this.current()?.value.toUpperCase() === 'WHEN'
    ) {
      this.index += 1
      const condition = this.parseOr()
      if (!this.consume('THEN')) return null
      const result = this.parseOr()
      if (!hasMatch && condition != null && condition !== 0) {
        matched = result
        hasMatch = true
      }
    }

    let fallback: number | null = null
    if (this.current()?.kind === 'identifier' && this.current()?.value.toUpperCase() === 'ELSE') {
      this.index += 1
      fallback = this.parseOr()
    }

    if (!this.consume('END')) return null
    return hasMatch ? matched : fallback
  }

  private applyFunction(name: string, args: Array<number | null>): number | null {
    const values = args.filter((value): value is number => value != null && Number.isFinite(value))

    switch (name) {
      case 'COALESCE':
        return (
          args.find((value): value is number => value != null && Number.isFinite(value)) ?? null
        )
      case 'NULLIF':
        return args.length >= 2 && args[0] != null && args[0] === args[1] ? null : (args[0] ?? null)
      case 'GREATEST':
      case 'MAX':
        return values.length === args.length && values.length > 0 ? Math.max(...values) : null
      case 'LEAST':
      case 'MIN':
        return values.length === args.length && values.length > 0 ? Math.min(...values) : null
      case 'ABS':
        return args[0] == null ? null : Math.abs(args[0])
      case 'ROUND':
        if (args[0] == null) return null
        {
          const digits = Math.trunc(args[1] ?? 0)
          if (!Number.isFinite(digits) || digits < 0 || digits > 100) {
            return null
          }
          return Number(args[0].toFixed(digits))
        }
      case 'FLOOR':
        return args[0] == null ? null : Math.floor(args[0])
      case 'CEIL':
        return args[0] == null ? null : Math.ceil(args[0])
      case 'POWER':
        return args[0] == null || args[1] == null ? null : Math.pow(args[0], args[1])
      case 'SQRT':
        return args[0] == null || args[0] < 0 ? null : Math.sqrt(args[0])
      case 'SIGN':
        return args[0] == null ? null : Math.sign(args[0])
      case 'TRUNC':
        if (args[0] == null) return null
        return args[1] == null
          ? Math.trunc(args[0])
          : Math.trunc(args[0] * 10 ** args[1]) / 10 ** args[1]
      default:
        return null
    }
  }
}

export const evaluatePayCurveFormula = (
  formula: string,
  context: { attainment: number; quota?: number; targetIncentive?: number }
): number | null => {
  const tokens = tokenizeFormula(formula.trim())
  if (!tokens?.length) return null

  return new PayCurveFormulaParser(tokens, {
    attainment: context.attainment,
    ...(context.quota == null ? {} : { quota: context.quota }),
    ...(context.targetIncentive == null
      ? {}
      : {
          target_incentive: context.targetIncentive,
          targetincentive: context.targetIncentive
        })
  }).parse()
}

export const resolvePayCurvePayout = (
  payout: PayCurvePayout,
  context: { attainment: number; quota?: number; targetIncentive?: number }
): number | null => {
  if (typeof payout === 'number') return isFiniteNumber(payout) ? payout : null

  const value = payout.trim()
  if (!value) return null
  if (/^-?(?:\d+\.?\d*|\.\d+)%$/.test(value)) return Number(value.slice(0, -1))
  if (/^-?(?:\d+\.?\d*|\.\d+)$/.test(value)) return Number(value)
  return evaluatePayCurveFormula(value, context)
}
export const normalizePayCurvePoints = (
  points: PayCurvePoint[] | null | undefined
): PayCurvePoint[] => {
  if (!Array.isArray(points)) return []

  return points
    .filter(point => {
      if (!point || typeof point !== 'object') return false
      const payout = point.payout
      const validPayout =
        typeof payout === 'number'
          ? isFiniteNumber(payout)
          : typeof payout === 'string' && payout.trim().length > 0
      return isFiniteNumber(point.attainment) && validPayout
    })
    .map(point => ({
      attainment: point.attainment,
      payout: point.payout
    }))
    .sort((a, b) => a.attainment - b.attainment)
    .reduce<PayCurvePoint[]>((result, point) => {
      const previous = result[result.length - 1]
      if (previous && Math.abs(previous.attainment - point.attainment) < EPSILON) {
        previous.payout = point.payout
      } else {
        result.push(point)
      }
      return result
    }, [])
}

const getFormulaContext = (config: PayCurveConfig, attainment: number) => ({
  attainment,
  quota: config.quota,
  targetIncentive: config.targetIncentive
})

const inferCapFromResolvedPoints = (points: ResolvedPayCurvePoint[]): number | null => {
  if (points.length < 2) return null

  const last = points[points.length - 1]
  const previous = points[points.length - 2]
  return Math.abs(last.payout - previous.payout) < EPSILON && last.payout > EPSILON
    ? last.payout
    : null
}

const resolvePayCurvePoints = (
  config: PayCurveConfig,
  applyCap = true
): ResolvedPayCurvePointWithSource[] => {
  const sourcePoints = normalizePayCurvePoints(config.points)
  const resolvedPoints = sourcePoints
    .map(point => ({
      attainment: point.attainment,
      sourcePayout: point.payout,
      payout: resolvePayCurvePayout(point.payout, getFormulaContext(config, point.attainment))
    }))
    .filter((point): point is ResolvedPayCurvePointWithSource => point.payout != null)

  const cap = applyCap ? inferCapFromResolvedPoints(resolvedPoints) : null
  return resolvedPoints.map(point => ({
    ...point,
    payout:
      cap == null ? clampNonNegative(point.payout) : Math.min(clampNonNegative(point.payout), cap)
  }))
}

export const interpolatePayoutAtAttainment = (
  points: ResolvedPayCurvePoint[],
  attainment: number
): number | null => {
  const normalized = points
    .filter(point => isFiniteNumber(point.attainment) && isFiniteNumber(point.payout))
    .sort((a, b) => a.attainment - b.attainment)
  if (!normalized.length || !isFiniteNumber(attainment)) return null

  if (attainment <= normalized[0].attainment) {
    return attainment < normalized[0].attainment && normalized[0].payout > 0
      ? 0
      : normalized[0].payout
  }

  const last = normalized[normalized.length - 1]
  if (attainment >= last.attainment) return last.payout

  const upperIndex = normalized.findIndex(point => point.attainment >= attainment)
  const upper = normalized[upperIndex]
  const lower = normalized[upperIndex - 1]
  const range = upper.attainment - lower.attainment

  if (!range) return upper.payout
  return lower.payout + ((attainment - lower.attainment) / range) * (upper.payout - lower.payout)
}

export const getPayCurvePayoutAtAttainment = (
  config: PayCurveConfig,
  attainment: number
): number | null => {
  if (!isFiniteNumber(attainment)) return null

  const sourcePoints = normalizePayCurvePoints(config.points)
  const uncappedPoints = resolvePayCurvePoints(config, false)
  if (!sourcePoints.length || uncappedPoints.length !== sourcePoints.length) {
    return null
  }

  const cap = inferCapFromResolvedPoints(uncappedPoints)
  const clampPayout = (value: number) =>
    cap == null ? clampNonNegative(value) : Math.min(clampNonNegative(value), cap)

  if (attainment < sourcePoints[0].attainment) return 0

  const exactIndex = sourcePoints.findIndex(
    point => Math.abs(point.attainment - attainment) < EPSILON
  )
  let payout: number | null

  if (exactIndex >= 0) {
    payout =
      typeof sourcePoints[exactIndex].payout === 'string'
        ? resolvePayCurvePayout(
            sourcePoints[exactIndex].payout,
            getFormulaContext(config, attainment)
          )
        : uncappedPoints[exactIndex].payout
  } else {
    const upperIndex = sourcePoints.findIndex(point => point.attainment > attainment)

    if (upperIndex < 0) {
      const lastIndex = sourcePoints.length - 1
      payout =
        typeof sourcePoints[lastIndex].payout === 'string'
          ? resolvePayCurvePayout(
              sourcePoints[lastIndex].payout,
              getFormulaContext(config, attainment)
            )
          : uncappedPoints[lastIndex].payout
    } else if (upperIndex === 0) {
      payout = 0
    } else {
      const lowerIndex = upperIndex - 1
      const lower = sourcePoints[lowerIndex]
      const upper = sourcePoints[upperIndex]

      if (typeof lower.payout === 'number' && typeof upper.payout === 'number') {
        payout = uncappedPoints[lowerIndex].payout
      } else {
        payout = interpolatePayoutAtAttainment(
          [uncappedPoints[lowerIndex], uncappedPoints[upperIndex]],
          attainment
        )
      }
    }
  }

  return payout == null ? null : clampPayout(payout)
}
export const mergePayCurveChartPoints = (
  chartPoints: ResolvedPayCurvePoint[],
  comparisonChartPoints: ResolvedPayCurvePoint[],
  config: PayCurveConfig,
  comparisonConfig?: PayCurveConfig
) => {
  const currentByAttainment = new Map<number, ResolvedPayCurvePoint[]>()
  const comparisonByAttainment = new Map<number, ResolvedPayCurvePoint[]>()

  for (const point of chartPoints) {
    const points = currentByAttainment.get(point.attainment) ?? []
    points.push(point)
    currentByAttainment.set(point.attainment, points)
  }

  for (const point of comparisonChartPoints) {
    const points = comparisonByAttainment.get(point.attainment) ?? []
    points.push(point)
    comparisonByAttainment.set(point.attainment, points)
  }

  const attainments = [
    ...new Set([...currentByAttainment.keys(), ...comparisonByAttainment.keys()])
  ].sort((left, right) => left - right)

  return attainments.flatMap(attainment => {
    const currentPoints = currentByAttainment.get(attainment) ?? []
    const comparisonPoints = comparisonByAttainment.get(attainment) ?? []
    const count = Math.max(currentPoints.length, comparisonPoints.length, 1)

    return Array.from({ length: count }, (_, index) => ({
      attainment,
      payout:
        currentPoints[index]?.payout ??
        currentPoints.at(-1)?.payout ??
        getPayCurvePayoutAtAttainment(config, attainment) ??
        0,
      comparisonPayout: comparisonConfig
        ? (comparisonPoints[index]?.payout ??
          comparisonPoints.at(-1)?.payout ??
          getPayCurvePayoutAtAttainment(comparisonConfig, attainment) ??
          0)
        : undefined
    }))
  })
}
const getQuantile = (values: number[], quantile: number): number | null => {
  if (!values.length) return null

  const index = (values.length - 1) * quantile
  const lowerIndex = Math.floor(index)
  const upperIndex = Math.ceil(index)
  if (lowerIndex === upperIndex) return values[lowerIndex]

  const weight = index - lowerIndex
  return values[lowerIndex] + (values[upperIndex] - values[lowerIndex]) * weight
}

export const getPayCurveProfileMetricValue = (
  profile: PayCurveSimulationProfile,
  config: PayCurveConfig
): number | null => {
  const quota = profile.quota ?? config.quota
  if (profile.metricValue == null && quota == null) return null

  const value = profile.metricValue ?? quota! * (profile.attainment / 100)

  return Number.isFinite(value) && value >= 0 ? value : null
}

export const getPayCurveProfileCompensation = (
  profile: PayCurveSimulationProfile,
  config?: PayCurveConfig
): number | null => {
  if (config?.targetIncentive == null) return null

  const payout = getPayCurvePayoutAtAttainment(config, profile.attainment)
  return payout == null ? null : (config.targetIncentive * payout) / 100
}

export const getPayCurveDistributionStats = (
  profiles: PayCurveSimulationProfile[],
  config: PayCurveConfig
): PayCurveSimulationAnalysis['distribution'] => {
  const attainments = profiles
    .map(profile => profile.attainment)
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
  const threshold = getPayCurveMetrics(config).threshold

  return {
    sampleSize: attainments.length,
    p25: getQuantile(attainments, 0.25),
    median: getQuantile(attainments, 0.5),
    p75: getQuantile(attainments, 0.75),
    threshold,
    thresholdHitRate:
      threshold == null || attainments.length === 0
        ? null
        : attainments.filter(attainment => attainment >= threshold).length / attainments.length
  }
}

export const getPayCurveExpenseStats = (
  profiles: PayCurveSimulationProfile[],
  config: PayCurveConfig
): PayCurveSimulationAnalysis['expense'] => {
  const validProfiles = profiles.filter(
    profile =>
      Number.isFinite(profile.attainment) && getPayCurveProfileMetricValue(profile, config) != null
  )
  if (!validProfiles.length) {
    return {
      sampleSize: 0,
      totalMetricValue: null,
      totalCompensation: null,
      compensationToMetricRate: null
    }
  }

  const totalMetricValue = validProfiles.reduce(
    (total, profile) => total + (getPayCurveProfileMetricValue(profile, config) ?? 0),
    0
  )
  const compensationValues = validProfiles.map(profile =>
    getPayCurveProfileCompensation(profile, config)
  )
  const resolvedCompensation = compensationValues.every((value): value is number => value != null)
    ? compensationValues
    : null
  const totalCompensation =
    config.targetIncentive == null || resolvedCompensation == null
      ? null
      : resolvedCompensation.reduce((total, compensation) => total + compensation, 0)

  return {
    sampleSize: validProfiles.length,
    totalMetricValue,
    totalCompensation,
    compensationToMetricRate:
      totalCompensation != null && totalMetricValue > 0
        ? totalCompensation / totalMetricValue
        : null
  }
}
export const getPayCurveMarginalPoints = (config: PayCurveConfig): PayCurveMarginalPoint[] => {
  const sourcePoints = normalizePayCurvePoints(config.points)
  const resolvedPoints = resolvePayCurvePoints(config)

  return resolvedPoints.slice(1).map((point, index) => {
    const previous = resolvedPoints[index]
    const previousSource = sourcePoints[index]
    const currentSource = sourcePoints[index + 1]
    const attainmentDelta = point.attainment - previous.attainment
    const payoutDelta = point.payout - previous.payout
    const isStep =
      typeof previousSource?.payout === 'number' && typeof currentSource?.payout === 'number'

    return {
      start: previous.attainment,
      end: point.attainment,
      marginalPayout: isStep || attainmentDelta <= 0 ? payoutDelta : payoutDelta / attainmentDelta
    }
  })
}

export const getPayCurveAttainmentHistogram = (
  profiles: PayCurveSimulationProfile[],
  binSize = 10
): PayCurveAttainmentBin[] => {
  if (!Number.isFinite(binSize) || binSize <= 0) return []

  const attainments = profiles.map(profile => profile.attainment).filter(Number.isFinite)
  if (!attainments.length) return []

  const maxAttainment = Math.max(100, ...attainments)
  const binCount = Math.floor(maxAttainment / binSize) + 1
  const bins = Array.from({ length: binCount }, (_, index) => ({
    start: index * binSize,
    end: (index + 1) * binSize,
    count: 0
  }))

  attainments.forEach(attainment => {
    const index = Math.min(bins.length - 1, Math.max(0, Math.floor(attainment / binSize)))
    bins[index].count += 1
  })

  return bins
}

export const getPayCurveProfileCompensationDeltas = (
  profiles: PayCurveSimulationProfile[],
  currentConfig: PayCurveConfig,
  comparisonConfig?: PayCurveConfig
): PayCurveProfileCompensationDelta[] =>
  profiles
    .filter(
      profile =>
        Number.isFinite(profile.attainment) &&
        getPayCurveProfileCompensation(profile, currentConfig) != null
    )
    .map(profile => {
      const currentCompensation = getPayCurveProfileCompensation(profile, currentConfig)
      const proposedCompensation = comparisonConfig
        ? getPayCurveProfileCompensation(profile, comparisonConfig)
        : null

      return {
        name: profile.name,
        attainment: profile.attainment,
        currentCompensation,
        proposedCompensation,
        delta:
          currentCompensation == null || proposedCompensation == null
            ? null
            : proposedCompensation - currentCompensation
      }
    })
    .sort((left, right) => (right.delta ?? 0) - (left.delta ?? 0))

export const getPayCurveSimulationAnalysis = (
  profiles: PayCurveSimulationProfile[],
  config: PayCurveConfig
): PayCurveSimulationAnalysis => ({
  distribution: getPayCurveDistributionStats(profiles, config),
  expense: getPayCurveExpenseStats(profiles, config)
})

export const getPayCurveSimulationComparison = (
  profiles: PayCurveSimulationProfile[],
  currentConfig: PayCurveConfig,
  comparisonConfig: PayCurveConfig
): PayCurveSimulationComparison => {
  const current = getPayCurveSimulationAnalysis(profiles, currentConfig)
  const comparison = getPayCurveSimulationAnalysis(profiles, comparisonConfig)

  return {
    current,
    comparison,
    delta: {
      totalCompensation:
        current.expense.totalCompensation == null || comparison.expense.totalCompensation == null
          ? null
          : comparison.expense.totalCompensation - current.expense.totalCompensation,
      compensationToMetricRate:
        current.expense.compensationToMetricRate == null ||
        comparison.expense.compensationToMetricRate == null
          ? null
          : comparison.expense.compensationToMetricRate - current.expense.compensationToMetricRate,
      thresholdHitRate:
        current.distribution.thresholdHitRate == null ||
        comparison.distribution.thresholdHitRate == null
          ? null
          : comparison.distribution.thresholdHitRate - current.distribution.thresholdHitRate
    }
  }
}

type PayCurveSegment = {
  end: number
  payoutDelta: number
  signal: number
  start: number
}

const getSegments = (points: ResolvedPayCurvePointWithSource[]): PayCurveSegment[] =>
  points.slice(1).map((point, index) => {
    const previous = points[index]
    const attainmentDelta = point.attainment - previous.attainment
    const payoutDelta = point.payout - previous.payout
    const isStep =
      typeof previous.sourcePayout === 'number' && typeof point.sourcePayout === 'number'
    return {
      start: previous.attainment,
      end: point.attainment,
      payoutDelta,
      signal: isStep ? payoutDelta : attainmentDelta > 0 ? payoutDelta / attainmentDelta : 0
    }
  })

const inferThreshold = (points: ResolvedPayCurvePoint[]) => {
  const firstPositiveIndex = points.findIndex(point => point.payout > EPSILON)
  if (firstPositiveIndex <= 0) return null

  const firstPositive = points[firstPositiveIndex]
  const previous = points[firstPositiveIndex - 1]
  return previous.payout <= EPSILON ? firstPositive.attainment : null
}

const inferCapAttainment = (points: ResolvedPayCurvePointWithSource[], cap: number | null) => {
  if (cap == null) return null

  const crossingIndex = points.findIndex(point => point.payout >= cap - EPSILON)
  if (crossingIndex < 0) return null
  if (crossingIndex === 0) return points[0].attainment

  const previous = points[crossingIndex - 1]
  const crossing = points[crossingIndex]
  const isStep =
    typeof previous.sourcePayout === 'number' && typeof crossing.sourcePayout === 'number'
  if (isStep || Math.abs(crossing.payout - previous.payout) < EPSILON) {
    return crossing.attainment
  }

  const payoutDelta = crossing.payout - previous.payout
  const fraction = (cap - previous.payout) / payoutDelta
  return (
    previous.attainment +
    Math.max(0, Math.min(1, fraction)) * (crossing.attainment - previous.attainment)
  )
}

export const getPayCurveMetrics = (config: PayCurveConfig): PayCurveMetrics => {
  const points = resolvePayCurvePoints(config)
  const uncappedPoints = resolvePayCurvePoints(config, false)
  const segments = getSegments(points)
  const threshold = inferThreshold(points)
  const cap = inferCapFromResolvedPoints(uncappedPoints)
  const capAttainment = inferCapAttainment(uncappedPoints, cap)

  const acceleration = segments.reduce<{
    change: number
    start: number
  } | null>((best, segment, index) => {
    const previousSignal = segments[index - 1]?.signal
    if (previousSignal == null) return best
    const change = segment.signal - previousSignal
    if (change <= EPSILON || (best && change <= best.change)) return best
    return { change, start: segment.start }
  }, null)

  const deceleratorBelowQuota = segments.some((segment, index) => {
    const previousSignal = segments[index - 1]?.signal
    return previousSignal != null && segment.signal + EPSILON < previousSignal && segment.end <= 100
  })

  const last = points[points.length - 1]
  const targetPayout = points.length ? getPayCurvePayoutAtAttainment(config, 100) : null
  const maxPayout = last?.payout ?? null
  const maxAttainment = last?.attainment ?? null

  return {
    points,
    threshold,
    targetPayout,
    cap,
    capAttainment,
    highestAccelerationStart: acceleration?.start ?? null,
    deceleratorBelowQuota,
    maxLeverage: maxPayout == null ? null : maxPayout / 100,
    maxAttainment,
    maxPayout
  }
}

export const getPayCurveDomainMax = (maxAttainment: number | null) => {
  const base = Math.max(100, maxAttainment ?? 100)
  const padding = Math.max(20, Math.ceil((base * 0.12) / 10) * 10)
  return base + padding
}

export const getPayCurveChartPoints = (config: PayCurveConfig): ResolvedPayCurvePoint[] => {
  const sourcePoints = normalizePayCurvePoints(config.points)
  const metrics = getPayCurveMetrics(config)
  if (!metrics.points.length) return []

  if (metrics.points.length !== sourcePoints.length) return metrics.points
  const chartPoints: ResolvedPayCurvePoint[] = []
  metrics.points.forEach((point, index) => {
    if (index === 0) {
      chartPoints.push(point)
      return
    }

    const previousSource = sourcePoints[index - 1]
    const currentSource = sourcePoints[index]
    if (typeof previousSource.payout === 'number' && typeof currentSource.payout === 'number') {
      chartPoints.push({
        attainment: point.attainment,
        payout: metrics.points[index - 1].payout
      })
    }
    chartPoints.push(point)
  })

  const last = chartPoints[chartPoints.length - 1]
  const domainMax = getPayCurveDomainMax(metrics.maxAttainment)
  if (domainMax <= last.attainment) return chartPoints

  const projectedPayout = getPayCurvePayoutAtAttainment(config, domainMax)
  return projectedPayout == null
    ? chartPoints
    : [...chartPoints, { attainment: domainMax, payout: projectedPayout }]
}
export const validatePayCurve = (config: PayCurveConfig): PayCurveValidationResult => {
  const errors: PayCurveValidationIssue[] = []
  const warnings: PayCurveValidationIssue[] = []
  const addIssue = (collection: PayCurveValidationIssue[], issue: PayCurveValidationIssue) => {
    collection.push(issue)
  }

  const parsed = PayCurveConfigSchema.safeParse(config)
  if (!parsed.success) {
    const rawConfig = config as unknown as
      | {
          points?: unknown
        }
      | null
      | undefined

    if (!Array.isArray(rawConfig?.points)) {
      addIssue(errors, {
        id: 'minimum-points',
        message: 'Add at least two breakpoints to define a curve.',
        severity: 'error'
      })
    }

    parsed.error.issues.forEach(issue => {
      const pointIndex =
        typeof issue.path[0] === 'string' &&
        issue.path[0] === 'points' &&
        typeof issue.path[1] === 'number'
          ? issue.path[1]
          : undefined
      const field = issue.path.at(-1)
      const id =
        field === 'attainment'
          ? 'invalid-attainment'
          : field === 'payout'
            ? 'invalid-payout'
            : field === 'quota'
              ? 'invalid-quota'
              : field === 'targetIncentive'
                ? 'invalid-target-incentive'
                : 'invalid-config'

      if (id === 'invalid-config' && pointIndex === undefined && issue.path[0] === 'points') {
        return
      }

      addIssue(errors, {
        id,
        message:
          id === 'invalid-attainment'
            ? 'Attainment must be a finite number at or above 0%.'
            : id === 'invalid-payout'
              ? 'Payout must be a finite number or formula.'
              : id === 'invalid-quota'
                ? 'Quota must be a finite number at or above 0.'
                : id === 'invalid-target-incentive'
                  ? 'Target incentive must be a finite number at or above 0.'
                  : 'Pay curve configuration has an invalid shape.',
        severity: 'error',
        ...(pointIndex == null ? {} : { pointIndex })
      })
    })

    return {
      errors,
      warnings,
      isValid: false
    }
  }

  const parsedConfig = parsed.data
  if (parsedConfig.points.length < 2) {
    addIssue(errors, {
      id: 'minimum-points',
      message: 'Add at least two breakpoints to define a curve.',
      severity: 'error'
    })
  }

  parsedConfig.points.forEach((point, pointIndex) => {
    if (point.attainment < 0) {
      addIssue(errors, {
        id: 'invalid-attainment',
        message: 'Attainment must be a finite number at or above 0%.',
        severity: 'error',
        pointIndex
      })
    }

    if (typeof point.payout === 'string') {
      const formula = point.payout.trim()
      const resolved = formula
        ? resolvePayCurvePayout(formula, {
            attainment: point.attainment,
            quota: parsedConfig.quota,
            targetIncentive: parsedConfig.targetIncentive
          })
        : null
      if (resolved == null || !isFiniteNumber(resolved) || resolved < 0) {
        addIssue(errors, {
          id: 'invalid-formula',
          message: 'Formula must evaluate to a finite payout percentage.',
          severity: 'error',
          pointIndex
        })
      }
    }
  })

  const normalized = normalizePayCurvePoints(parsedConfig.points)
  if (normalized.length < parsedConfig.points.length) {
    addIssue(warnings, {
      id: 'duplicate-attainment',
      message: 'Breakpoints with the same attainment are collapsed to the last value.',
      severity: 'warning'
    })
  }

  if (normalized.length >= 2) {
    if (normalized[0].attainment > 0) {
      addIssue(warnings, {
        id: 'missing-zero-anchor',
        message:
          'No 0% breakpoint exists; payout is assumed to be zero before the first breakpoint.',
        severity: 'warning'
      })
    }

    if (!normalized.some(point => Math.abs(point.attainment - 100) < EPSILON)) {
      addIssue(warnings, {
        id: 'missing-quota-anchor',
        message: 'No 100% breakpoint exists; quota payout is derived from the surrounding curve.',
        severity: 'warning'
      })
    }

    const resolved = resolvePayCurvePoints(parsedConfig, false)
    for (let index = 1; index < resolved.length; index += 1) {
      if (resolved[index].payout + EPSILON < resolved[index - 1].payout) {
        addIssue(warnings, {
          id: 'decreasing-payout',
          message: 'Payout decreases at a later breakpoint; review this intentional decelerator.',
          severity: 'warning',
          pointIndex: index
        })
      }
    }
  }

  return {
    errors,
    warnings,
    isValid: errors.length === 0
  }
}

export class PayCurveValidationError extends Error {
  constructor(public readonly issues: PayCurveValidationIssue[]) {
    super('Pay curve configuration is invalid.')
    this.name = 'PayCurveValidationError'
  }
}

export const assertValidPayCurve = (input: unknown): PayCurveConfig => {
  const parsed = PayCurveConfigSchema.safeParse(input)
  if (!parsed.success) {
    throw new PayCurveValidationError([
      {
        id: 'invalid-config',
        message: 'Pay curve configuration has an invalid shape.',
        severity: 'error'
      }
    ])
  }

  const validation = validatePayCurve(parsed.data)
  if (!validation.isValid) {
    throw new PayCurveValidationError(validation.errors)
  }

  return parsed.data
}

export const assertValidSimulationProfiles = (input: unknown): PayCurveSimulationProfile[] => {
  const parsed = PayCurveSimulationProfilesSchema.safeParse(input)
  if (!parsed.success) {
    throw new PayCurveValidationError([
      {
        id: 'invalid-simulation-profiles',
        message: 'Simulation profiles have an invalid shape.',
        severity: 'error'
      }
    ])
  }

  return parsed.data
}
